import React, { useMemo } from "react";
import { AppBar, IconButton, Stack, Toolbar } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import Button from "components/basic/button";
import MenuIcon from "@mui/icons-material/Menu";
import Typography from "components/basic/typography";
import { useMeQuery } from "generated/graphql";
import { useTranslation } from "react-i18next";
import { ParseKeys } from "i18next";
import LanguageSwitcher from "components/basic/languageSwitcher";

const ROOT = "/";

// Keeps the brand title centred
const MENU_ITEM_WIDTH = 50;
export const DEFAULT_MENU_HEIGHT = 50;

/**
 * The height above page content, plus `extraPx`, for `calc()`: the header
 * and, while one shows, the status banner below it (which sets the variable).
 */
export const headerOffset = (extraPx = 0): string =>
  `(${DEFAULT_MENU_HEIGHT + extraPx}px + var(--status-banner-height, 0px))`;

export interface HeaderItem {
  label: ParseKeys<"common">;
  shortenedLabel: ParseKeys<"common">;
  path: string;
}

export interface HeaderProps {
  onMenuClick: () => void;
}

const Header: React.FC<HeaderProps> = ({ onMenuClick }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // TODO - Make storybook work with useLocation
  const location = useLocation();
  const currentPath = location.pathname;

  const [{ data: meQueryData, fetching: fetchingUser }] = useMeQuery();

  const loginButton = useMemo(() => {
    if (fetchingUser) {
      return null;
    }

    if (!meQueryData?.me) {
      return (
        <Button
          variant="text"
          color="secondary"
          onClick={() => navigate("/signin")}
        >
          {t("nav.login")}
        </Button>
      );
    }

    const user = meQueryData.me;

    return (
      <Button variant="text" color="secondary" onClick={() => {}}>
        {user.firstName} {user.lastName}
      </Button>
    );
  }, [meQueryData, fetchingUser, navigate, t]);

  if ([ROOT].includes(currentPath)) {
    return (
      <AppBar position="sticky">
        <Toolbar style={{ minHeight: `${DEFAULT_MENU_HEIGHT}px` }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              flexGrow: 1,
              width: `${MENU_ITEM_WIDTH}px`,
            }}
          >
            <IconButton onClick={onMenuClick}>
              <MenuIcon fontSize="large" color="secondary" />
            </IconButton>

            <Typography variant="h4">Piper&apos;s Flute</Typography>
          </div>

          <Stack direction="row" spacing={2} style={{ overflow: "auto" }}>
            <LanguageSwitcher />
            {loginButton}
          </Stack>
        </Toolbar>
      </AppBar>
    );
  }

  if (["/login", "/register", "/signin", "/signup"].includes(currentPath)) {
    return (
      <AppBar position="static">
        <Toolbar style={{ minHeight: `${DEFAULT_MENU_HEIGHT}px` }}>
          <div style={{ flexGrow: 1, width: `${MENU_ITEM_WIDTH}px` }}>
            <IconButton onClick={onMenuClick}>
              <MenuIcon fontSize="large" color="secondary" />
            </IconButton>
          </div>

          <div style={{ flexGrow: 1, marginLeft: `-${MENU_ITEM_WIDTH}px` }}>
            <Typography variant="h4">Piper&apos;s Flute</Typography>
          </div>

          <LanguageSwitcher />
        </Toolbar>
      </AppBar>
    );
  }

  // Just the hamburger. The tools live in the drawer under TOOLS rather than
  // inline, so the bar does not need to reflow as the tool list changes.
  return (
    <AppBar position="static">
      <Toolbar style={{ minHeight: `${DEFAULT_MENU_HEIGHT}px` }}>
        <IconButton onClick={onMenuClick} aria-label={t("nav.open_menu")}>
          <MenuIcon fontSize="large" color="secondary" />
        </IconButton>

        <Stack direction="row" sx={{ marginLeft: "auto" }}>
          <LanguageSwitcher />
        </Stack>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
